import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ZfFileItem, ZfFilesUpload, ZfFormField, ZfTypography } from '@zafire/react';
import { EnvConfig } from 'config/envConfig';
import { useFormikContext } from 'formik';
import { GlobalState } from 'reducers/stateTypes';
import { CN_MGMT_API_URL } from 'services/constants';
import { User } from 'types/User';
import { base64ToArrayBuffer } from 'utils/arrayBufferUtil';
import { DeserialisedContactNote } from 'utils/noteDialogUtil';
import { FileInfo } from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';
import { MAX_TOTAL_UPLOAD_SIZE } from 'utils/constantUtil';
import { isUnsupportedAttachmentFormat } from 'utils/attachmentFormatUtil';
import { useGetSupportedAttachmentFormats } from 'hooks/useGetSupportedAttachmentFormats';

const DropZone = ({ nonEditable = false }) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<DeserialisedContactNote>();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);

  const { t } = useTranslation();
  // Keeps the format list in sync for both this list and the save validation. Only drag-and-drop
  // notes are archived to OnDemand, so only they are restricted to the supported formats.
  const { data: supportedFormats = [] } = useGetSupportedAttachmentFormats();
  const isDragAndDrop = !!values.dragAndDrop;
  // const [uploadedList, setShowUploadedList] = useState<FileInfo[]>([]);
  // const [uploadFileLength, setUploadFileLength] = useState<number | undefined>();

  /**
   * The error shown on a single row. Format is checked here rather than on insert so that email
   * attachments, which are prefilled into the form and never pass through onFileInputChange, are
   * flagged the same way as files the user picked.
   */
  const getAttachmentError = (item: FileInfo) => {
    if (item.error) {
      return item.error;
    }
    if (isDragAndDrop && isUnsupportedAttachmentFormat(item.name || item.description, supportedFormats)) {
      return t('contactNote.form.upload.fileFormatUnsupported');
    }
    return '';
  };

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const onFileInputChange = async (event: File[]) => {
    const fileList = [];
    for (const fileItem of event) {
      if (fileItem.size > MAX_TOTAL_UPLOAD_SIZE) {
        const fileObject = {
          ...fileItem,
          name: fileItem.name,
          description: fileItem.name,
          error: t('contactNote.form.upload.fileSizeExceed'),
          size: fileItem.size,
        };
        fileList.push(fileObject);
        continue;
      }
      const data = await toBase64(fileItem);
      const fileObject = {
        ...fileItem,
        description: fileItem.name,
        b64Data: (data as string)?.split(',')[1],
        data,
        file: fileItem,
        name: fileItem.name,
        size: fileItem.size,
      };
      fileList.push(fileObject);
    }
    setFieldValue('attachments', [...values.attachments, ...fileList]);
  };

  const onDelete = useCallback(
    (fileItem: FileInfo, index: number) => {
      if (EnvConfig.config.createContactNoteNewUiEnabled) {
        if (user.hasNoteCreationAccess || (user.hasNoteEditAccess && values?.attachments)) {
          const selectedFiles = values.attachments?.[index];
          if (selectedFiles.name === fileItem.name) {
            const existingFiles = values?.attachments.filter((_, i) => i !== index);
            setFieldValue('attachments', existingFiles);
          }
        }
        return;
      }
    },
    [
      EnvConfig.config.createContactNoteNewUiEnabled,
      user.hasNoteCreationAccess,
      user.hasNoteEditAccess,
      values?.attachments,
      setFieldValue,
    ],
  );

  const onFileDownload = useCallback((fileItem: FileInfo) => {
    const isFileEmail = fileItem.description?.endsWith('.msg');
    const isEmailAttachment = Object.prototype.hasOwnProperty.call(fileItem, 'id') && !(fileItem as FileInfo).id;
    const isFileNewUpload = !Object.prototype.hasOwnProperty.call(fileItem, 'id');

    if (isEmailAttachment) {
      const blob = new Blob([base64ToArrayBuffer((fileItem as FileInfo).b64Data)], {
        type: (fileItem as FileInfo).mimeType,
      });
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    } else if (isFileNewUpload) {
      fileItem.file.arrayBuffer().then((arrayBuffer: ArrayBuffer) => {
        const blob = new Blob([arrayBuffer], {
          type: isFileEmail ? 'application/vnd.ms-outlook' : fileItem.file.type,
        });
        const blobURL = URL.createObjectURL(blob);
        if (isFileEmail) {
          const link = document.createElement('a');
          link.href = blobURL;
          link.download = fileItem.description as string;
          document.body.append(link);
          link.click();
          link.remove();
        } else {
          window.open(blobURL);
        }
      });
    } else {
      const url = `${CN_MGMT_API_URL}/frontend/notes/attachments/${(fileItem as FileInfo).id}`;
      if (fileItem.description?.endsWith(`.pdf`)) {
        window.open(url);
      } else {
        const link = document.createElement('a');
        link.download = fileItem.description as string;
        link.href = url;
        link.click();
      }
    }
  }, []);

  return (
    <ZfFormField alerts={[errors.attachments?.toString() ?? '']}>
      <ZfFilesUpload
        maxFilesCount={9}
        text="Drag files here or click to upload"
        hasDropzone
        onFileInputChange={(event) => onFileInputChange(event.detail)}
        error={!!(touched.attachments && errors.attachments)}
        disabled={nonEditable}
        data-testid="contact-note-attachments-dropzone"
      >
        {values.attachments.map((item, index) => {
          const attachmentError = getAttachmentError(item);
          return (
            <ZfFileItem
              key={`${item.description}_${index}`}
              name={item.name || item.description}
              downloadable={!attachmentError}
              deletable={!nonEditable}
              onFileDownload={() => onFileDownload(item)}
              onFileDelete={() => onDelete(item, index)}
              progress={attachmentError ? 'error' : 'uploaded'}
              errorText={attachmentError}
            />
          );
        })}
      </ZfFilesUpload>
      {values.attachments.length > 9 && (
        <ZfTypography variant="label-small" color="danger">
          {t('contactNote.form.upload.fileCountExceed')}
        </ZfTypography>
      )}
    </ZfFormField>
  );
};

export default DropZone;
